import { createWithEqualityFn } from 'zustand/traditional';
import { ScholarshipsResult } from '@common-types';

import { baseService } from '@services/base.service';

import { ResultFilters } from '@store/types/filters';

import { INITIAL_VALUE, SortByValues } from '@pages/Scholarships/components/SortBy/SortBy';

import { getQueryParams } from '@utils/scholarships';

import {
  AwardsFilter,
  LevelOfStudyFilter,
  RequirementsFilter,
  TypeFilter,
  InstitutionTypeFilter,
  AwardClassificationFilter,
  AreasOfStudyFilter,
  EthnicityFilter,
  OtherCategoriesFilter,
  DisabilityFilter,
} from '@interfaces/allScholarships';
import { AutocompleteOption } from '@interfaces';

import { DESKTOP_PER_PAGE_OPTIONS } from '@constants';
import { ScholarshipSortOption, getStateSpotlightLocationParams } from '@constants/stateSpotlight';
import { withUtahBadges } from '@api/stateSpotlight';
import { INITIAL_MAIN_FILTERS } from '@constants/allScholarships';

export interface MainFilters
  extends RequirementsFilter,
    TypeFilter,
    LevelOfStudyFilter,
    InstitutionTypeFilter,
    AwardClassificationFilter,
    AreasOfStudyFilter,
    EthnicityFilter,
    OtherCategoriesFilter,
    DisabilityFilter {
  keywords?: string;
  moneyRange?: number[];
  stateResidence?: AutocompleteOption[];
  statePublicCollege?: string[];
  restOfUs?: string[];
}

export interface ScholarshipListFilters extends MainFilters, ResultFilters {}

interface ScholarshipList {
  scholarshipsData: ScholarshipsResult;
  filters: ScholarshipListFilters;
  awardsFilter: AwardsFilter;
  sortByValues: SortByValues<ScholarshipSortOption>;
  isLoading: boolean;

  getScholarshipsData: (mainFilters: MainFilters) => void;
  getAwardsRange: () => void;
  setSortByFields: (sortBy: SortByValues<ScholarshipSortOption>) => void;
  setScholarshipFilters: (searchFilters: ScholarshipListFilters) => void;
  removeScholarshipFilter: (searchFilter: keyof ScholarshipListFilters) => void;
  removeScholarshipFilterValue: (searchFilter: keyof ScholarshipListFilters) => void;
  resetScholarshipFilters: () => void;
}

const initialState = {
  scholarshipsData: {} as ScholarshipsResult,
  filters: INITIAL_MAIN_FILTERS as ScholarshipListFilters,
  awardsFilter: {
    step: 1,
    minLimit: 0,
    maxLimit: 100,
  } as AwardsFilter,
  sortByValues: INITIAL_VALUE as SortByValues<ScholarshipSortOption>,
  isLoading: false,
};

const getSortParams = (sortByValues: SortByValues<ScholarshipSortOption>): Record<string, unknown> => ({
  sort: sortByValues.sort,
  sortDirection: sortByValues.sortDirection,
});

let latestScholarshipsRequestId = 0;

export const useScholarshipListStore = createWithEqualityFn<ScholarshipList>()(
  (set, get) => ({
    ...initialState,

    getScholarshipsData: async (mainFilters: MainFilters) => {
      const requestId = ++latestScholarshipsRequestId;

      set({ isLoading: true });

      const { filters, sortByValues } = get();
      const { skip, limit } = filters;

      const params: Record<string, unknown> = {
        limit: limit || DESKTOP_PER_PAGE_OPTIONS[0],
        ...getSortParams(sortByValues),
      };

      if (skip) {
        params.skip = skip;
      }

      Object.assign(params, getStateSpotlightLocationParams(mainFilters as Record<string, unknown>));

      const allParams = { ...params, ...getQueryParams(mainFilters) };

      const scholarshipsData = await baseService.getAsync<ScholarshipsResult>(`/scholarship-cards`, {
        params: allParams,
      });

      if (requestId !== latestScholarshipsRequestId) return;

      set({
        scholarshipsData: withUtahBadges(scholarshipsData),
        isLoading: false,
      });
    },

    getAwardsRange: async () => {
      if (get().awardsFilter.minLimit && get().awardsFilter.maxLimit) return;

      const { minLimit, maxLimit } = await baseService.getAsync<{
        minLimit: number;
        maxLimit: number;
      }>('/scholarship-awards');

      set({
        awardsFilter: {
          minLimit,
          maxLimit,
          step: 25,
        },
      });
    },

    setScholarshipFilters: (searchFilters: ScholarshipListFilters) => {
      set({
        filters: { ...get().filters, ...searchFilters },
      });
    },

    setSortByFields: (sortByValues: SortByValues<ScholarshipSortOption>) => {
      set({
        sortByValues,
      });
    },

    resetScholarshipFilters: () => {
      set({
        filters: INITIAL_MAIN_FILTERS,
      });
    },

    removeScholarshipFilter: (searchFilter: keyof ScholarshipListFilters) => {
      const filters = { ...get().filters };

      delete filters[searchFilter];

      set({
        filters,
      });
    },

    removeScholarshipFilterValue: (searchFilter: keyof ScholarshipListFilters) => {
      set({
        filters: { ...get().filters, [searchFilter]: INITIAL_MAIN_FILTERS[searchFilter as string] },
      });
    },
  }),
  Object.is,
);
